import React, { useEffect, useState } from 'react';
import { Layout, Menu, Badge, Drawer, Input } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  GiftOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileProtectOutlined,
  ShoppingCartOutlined,
  FileAddOutlined,
  InboxOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  ToolOutlined,
  PieChartOutlined,
  UnorderedListOutlined,
  CloudUploadOutlined,
  SettingOutlined,
  ScheduleOutlined,
  SafetyOutlined,
  AuditOutlined,
  UserOutlined,
  HomeOutlined,
  ApartmentOutlined,
  DollarOutlined,
  DatabaseOutlined,
  MedicineBoxOutlined,
  ExperimentOutlined,
  SwapOutlined,
  ProfileOutlined,
  CheckSquareOutlined,
  SearchOutlined,
  UserAddOutlined,
  PlusOutlined,
  FlagOutlined,
  HistoryOutlined,
  EyeInvisibleOutlined,
  ShopOutlined,
  PrinterOutlined,
  DesktopOutlined,
  TagsOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
  UnlockOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import api from '../utils/api';
import { canSee, isAdmin, screensLoaded, serverMenu } from '../utils/roles';

const { Sider } = Layout;

/** Icons stay on the client; keys/labels/order come from backend/core/screens.py via /auth/screens/. */
const MENU_ICONS = {
  '/home': <HomeOutlined />,
  '/my-tasks': <CheckCircleOutlined />,
  '/purchase-requests': <FileAddOutlined />,
  '/horses/new': <PlusOutlined />,
  '/horses': <UnorderedListOutlined />,
  '/horse-health': <MedicineBoxOutlined />,
  '/treatments': <ExperimentOutlined />,
  '/horse-movements': <SwapOutlined />,
  '/horse-activities': <ApartmentOutlined />,
  '/cashier-sales': <ShopOutlined />,
  '/clubsys-import': <CloudUploadOutlined />,
  '/timetable': <ScheduleOutlined />,
  '/gate': <SafetyOutlined />,
  '/pony-rides': <TagsOutlined />,
  '/school/day-end': <CheckSquareOutlined />,
  '/school/client-review': <UserAddOutlined />,
  '/school/compensations': <GiftOutlined />,
  '/coach-day': <UserOutlined />,
  '/school-control': <AuditOutlined />,
  '/livery': <HomeOutlined />,
  '/feed-transfer': <AppstoreOutlined />,
  '/feed-sheet': <CalendarOutlined />,
  '/feed-give': <CheckSquareOutlined />,
  '/feed-horse': <SearchOutlined />,
  '/feed-plan': <ToolOutlined />,
  '/bedding': <InboxOutlined />,
  '/feed-night-weigh': <FileProtectOutlined />,
  '/distribution': <AppstoreOutlined />,
  '/service-records': <ToolOutlined />,
  '/service-schedules': <CalendarOutlined />,
  '/lpos': <ShoppingCartOutlined />,
  '/goods-receipts': <InboxOutlined />,
  '/supplier-invoices': <FileDoneOutlined />,
  '/items': <TagsOutlined />,
  '/stock-control': <DatabaseOutlined />,
  '/farrier-stock': <DatabaseOutlined />,
  '/stock-cover': <AlertOutlined />,
  '/yard-stock': <InboxOutlined />,
  '/reports': <BarChartOutlined />,
  '/horse-statistics': <PieChartOutlined />,
  '/horse-month-compare': <HistoryOutlined />,
  '/horse-cost-revenue': <DollarOutlined />,
  '/line-of-business': <PieChartOutlined />,
  '/coach-pnl': <DollarOutlined />,
  '/schooling-direct': <DollarOutlined />,
  '/yard/structure': <HomeOutlined />,
  '/yard/places': <TeamOutlined />,
  '/horse-workload': <BarChartOutlined />,
  '/feed-variance-report': <PieChartOutlined />,
  '/service-cost-dashboard': <PieChartOutlined />,
  '/variance-report': <BarChartOutlined />,
  '/setup/horse-lists': <ProfileOutlined />,
  '/school-setup': <SettingOutlined />,
  '/school/opening': <FlagOutlined />,
  '/cost-centres': <ApartmentOutlined />,
  '/admin/core/user/': <TeamOutlined />,
  '/screens': <EyeInvisibleOutlined />,
  '/print-settings': <PrinterOutlined />,
  '/who-is-using': <DesktopOutlined />,
  '/open-close-period': <UnlockOutlined />,
  '/admin/': <SettingOutlined />,
};

const ADMIN_GROUP = {
  key: 'admin',
  label: 'Users & Admin',
  adminOnly: true,
  children: [
    { key: '/admin/core/user/', label: 'Users & Roles' },
    { key: '/screens', label: 'Screens & user permissions' },
    { key: '/print-settings', label: 'Print & receipt' },
    { key: '/who-is-using', label: 'Who is signed in' },
    { key: '/admin/', label: 'System Admin' },
  ],
};

const buildGroups = () => {
  const fromServer = serverMenu();
  const groups = [];
  if (fromServer) {
    fromServer.forEach((g) => {
      const children = [];
      (g.items || []).forEach((it) => {
        if (it.children && it.children.length) {
          children.push({
            key: `submenu-${it.key}`,
            label: it.label,
            iconKey: it.key,
            children: it.children.map((ch) => ({ key: ch.key, label: ch.label })),
          });
        } else {
          children.push({ key: it.key, label: it.label });
        }
      });
      if (g.group === 'Home') {
        const homeAt = children.findIndex((c) => c.key === '/home');
        const taskItem = { key: '/my-tasks', label: 'My tasks', always: true };
        if (homeAt >= 0) children.splice(homeAt + 1, 0, taskItem);
        else children.unshift(taskItem);
      }
      groups.push({ key: g.group.toLowerCase().replace(/\s+/g, '-'), label: g.group, children });
    });
  }
  groups.push(ADMIN_GROUP);
  return groups;
};

const allowed = (item) => {
  if (item.adminOnly) return isAdmin();
  if (item.children || item.always) return true;
  return !screensLoaded() || canSee(item.key);
};

/** On phones the menu opens as a drawer (`open`/`onClose`); `footer` holds the account buttons there. */
const Sidebar = ({ mobile, open, onClose, footer }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [waiting, setWaiting] = useState({ today: 0, waiting: 0 });
  const path = location.pathname;
  const [openKeys, setOpenKeys] = useState([]);
  const [query, setQuery] = useState('');
  const [searchOn, setSearchOn] = useState(false);

  useEffect(() => {
    let live = true;
    let last = 0;
    const load = () => {
      const now = Date.now();
      if (now - last < 30000) return;
      last = now;
      api.get('/auth/my-tasks/').then((r) => live && setWaiting({
        today: r.data.today_total || 0, waiting: r.data.waiting_kinds || 0,
      })).catch(() => {});
    };
    load();
    const t = setInterval(load, 120000);
    return () => { live = false; clearInterval(t); };
  }, [location.pathname]);

  useEffect(() => {
    api.get('/auth/users/me/').then((r) => {
      let preview = false;
      try { preview = !!JSON.parse(localStorage.getItem('user') || '{}').preview; } catch (e) { preview = false; }
      setSearchOn(!!(r.data.menu_search && !preview && !r.data.preview));
    }).catch(() => {});
  }, []);

  const menuItems = buildGroups().filter((g) => allowed(g)).map((g) => {
    const children = g.children.filter((c) => allowed(c)).map((c) => {
      const icon = MENU_ICONS[c.iconKey || c.key];
      const badgeN = waiting.today || waiting.waiting;
      const label = c.key === '/my-tasks'
        ? (
          <span>
            {c.label}{' '}
            <Badge count={badgeN} overflowCount={9999} size="small" style={{ marginLeft: 4 }}
              color={waiting.today ? undefined : (waiting.waiting ? 'gold' : undefined)} />
          </span>
        )
        : c.label;
      if (c.children && c.children.length) {
        const kids = c.children.filter((k) => !screensLoaded() || canSee(k.key));
        if (!kids.length) return null;
        return { key: c.key, icon, label, search: c.label, children: kids.map((k) => ({ key: k.key, label: k.label, search: k.label })) };
      }
      return { key: c.key, icon, label, search: c.label };
    }).filter(Boolean);
    if (!children.length) return null;
    return { key: g.key, className: `menu-sec menu-sec-${g.key}`, label: g.label, children };
  }).filter(Boolean);

  const hits = [];
  if (searchOn && query.trim()) {
    const q = query.trim().toLowerCase();
    const walk = (items, section) => {
      (items || []).forEach((it) => {
        if (it.children) walk(it.children, section);
        const name = it.search || '';
        if (name && String(it.key).startsWith('/') && name.toLowerCase().includes(q)) hits.push({ key: it.key, name, section });
      });
    };
    menuItems.forEach((g) => walk(g.children, typeof g.label === 'string' ? g.label : ''));
  }

  const hash = (location.hash || '').replace('#', '');
  let selected = path;
  if (path === '/horses') selected = hash && hash !== 'all' ? `/horses#${hash}` : '/horses#all';
  else if (path.startsWith('/horses/')) selected = '/horses#all';
  else if (path.startsWith('/reports/')) selected = '/reports';

  const menu = (
    <div className="app-sider">
    {searchOn && (
      <div style={{ padding: '8px 12px 4px' }}>
        <Input allowClear size="small" placeholder="Search the menu" value={query} onChange={(e) => setQuery(e.target.value)} />
        {query.trim() && (
          <div className="menu-search-hits">
            {hits.length ? hits.map((h) => (
              <button key={h.key} type="button" className="menu-search-hit" onClick={() => {
                if (h.key.startsWith('/admin/')) window.open(h.key, '_blank');
                else if (h.key === '/horses#all') navigate('/horses');
                else navigate(h.key);
                setQuery('');
                if (mobile) onClose();
              }}>
                <span>{h.name}</span>
                <small>{h.section}</small>
              </button>
            )) : <div className="menu-search-empty">No page with that name</div>}
          </div>
        )}
      </div>
    )}
    {!query.trim() && <Menu
      mode="inline"
      selectedKeys={[selected]}
      openKeys={openKeys}
      onOpenChange={setOpenKeys}
      style={{ borderRight: 0 }}
      items={menuItems}
      onClick={({ key }) => {
        if (key.startsWith('/admin/')) window.open(key, '_blank');
        else if (key === 'submenu-/horses' || key === '/horses#all') navigate('/horses');
        else if (!key.startsWith('submenu-')) navigate(key);
        if (mobile) onClose();
      }}
    />}
    </div>
  );

  if (mobile) {
    return (
      <Drawer placement="left" open={open} onClose={onClose} width={300} title="ASHR" footer={footer}
        styles={{ body: { padding: 0 }, footer: { padding: 12 } }}>
        {menu}
      </Drawer>
    );
  }
  return (
      <Sider width={248} style={{ background: '#fff', overflowY: 'auto', height: '100vh', position: 'sticky', top: 0 }}>
      {menu}
    </Sider>
  );
};

export default Sidebar;
